/**
 * Supabase Client (Client-Side)
 *
 * Creates a Supabase client for browser-side usage using @supabase/supabase-js.
 * Uses VITE_ prefixed environment variables.
 *
 * Features:
 * - Auto token refresh
 * - Session persistence
 * - OAuth callback detection
 * - PKCE flow for security
 */

import type { Database } from '@cortex/database';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/**
 * Whether Supabase environment variables are configured
 */
export function isSupabaseConfigured(): boolean {
  return Boolean(supabaseUrl && supabaseAnonKey);
}

/**
 * Supabase client singleton for browser usage.
 * Falls back to empty strings if env vars are missing to avoid
 * crashing at module load time. Use isSupabaseConfigured() to
 * check before making API calls.
 */
export const supabase = createClient<Database>(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  }
);
